import { BlogDAO } from './dao/BlogDAO.js';
import { AuthorDAO } from './dao/AuthorDAO.js';
import { TagDAO } from './dao/TagDAO.js';
import { marked } from 'marked';

export class BlogService {
  constructor() {
    this.blogDAO = new BlogDAO();
    this.authorDAO = new AuthorDAO();
    this.tagDAO = new TagDAO(this.blogDAO);
    this.transformCache = new Map();
  }

  // CORE BLOG POST OPERATIONS

  // Get single blog post with full details
  async getPost(slug, options = {}) {
    const { showFuture = false, includeContent = false } = options;
    
    try {
      const blogDb = await this.blogDAO.getBlogDatabase();
      const routingEntry = this.blogDAO.getRoutingEntry(blogDb.routing, slug);
      
      if (!routingEntry) return null;

      // Load story metadata
      const storyData = await this.blogDAO.getStoryMetadata(
        routingEntry.storyId, 
        routingEntry.partFolder
      );
      
      if (!storyData) return null;

      // Find story reference in database
      const storyRef = blogDb.stories.find(s => s.id === routingEntry.storyId);
      if (!storyRef) return null;

      // Check publish status
      if (!this.blogDAO.shouldShow(storyRef.publish_date, storyRef.publishStatus, showFuture)) {
        return null;
      }

      // Get series data if needed
      let seriesData = null;
      if (routingEntry.type === 'series') {
        seriesData = await this.blogDAO.getSeriesMetadata(routingEntry.storyId);
      }

      // Transform to blog post
      const post = await this.transformToPost(storyData, storyRef, routingEntry, seriesData);
      post.slug = slug;
      post.url = `/blog/${slug}`;

      // Add content if requested
      if (includeContent) {
        const markdown = await this.blogDAO.getStoryContent(
          routingEntry.storyId, 
          routingEntry.partFolder
        );
        const html = this.processMarkdownToHtml(markdown);
        post.content = {
          markdown,
          html,
          wordCount: this.getWordCount(markdown),
          estimatedReadTime: this.calculateReadTime(markdown)
        };
        // Also add html directly to post for backward compatibility
        post.html = html;
      }

      return post;
    } catch (error) {
      console.error(`Failed to get post ${slug}:`, error);
      return null;
    }
  }

  // Get blog post content (markdown)
  async getPostContent(slug) {
    try {
      const blogDb = await this.blogDAO.getBlogDatabase();
      const routingEntry = this.blogDAO.getRoutingEntry(blogDb.routing, slug);
      
      if (!routingEntry) return null;

      return await this.blogDAO.getStoryContent(
        routingEntry.storyId, 
        routingEntry.partFolder
      );
    } catch (error) {
      console.error(`Failed to get post content ${slug}:`, error);
      return null;
    }
  }

  // LIST AND FILTERING OPERATIONS

  // Get blog posts with advanced filtering and pagination
  async getPosts(options = {}) {
    const {
      category = null,
      tag = null,
      author = null,
      featured = null,
      showFuture = false,
      limit = null,
      offset = 0,
      sortBy = 'date',
      sortOrder = 'desc'
    } = options;

    try {
      const blogDb = await this.blogDAO.getBlogDatabase();
      
      // Build list of posts to process
      const postsToProcess = [];
      const currentDate = new Date();

      // Process all stories
      for (const story of blogDb.stories) {
        // Check basic filters
        if (!this.blogDAO.shouldShow(story.publish_date, story.publishStatus, showFuture)) {
          continue;
        }
        
        if (category && story.category !== category) continue;
        if (author && story.author_slug !== author) continue;
        if (featured !== null && Boolean(story.featured) !== featured) continue;

        // Tag filtering
        if (tag) {
          const tagIndex = blogDb.tags.indexOf(tag);
          if (tagIndex === -1 || !story.tags || !story.tags.includes(tagIndex)) {
            continue;
          }
        }

        // Add story to processing list
        if (story.type === 'standalone') {
          postsToProcess.push({
            storyId: story.id,
            partFolder: null,
            publishDate: story.publish_date,
            story
          });
        } else if (story.type === 'series') {
          // For series, we need to load series data to get parts
          const seriesData = await this.blogDAO.getSeriesMetadata(story.id);
          if (seriesData) {
            // Add series overview if it exists
            if (seriesData.overviewStory) {
              postsToProcess.push({
                storyId: story.id,
                partFolder: seriesData.overviewStory,
                publishDate: story.publish_date,
                story,
                seriesData,
                partNumber: 0 // Overview is part 0
              });
            }
            
            // Add individual parts
            if (seriesData.parts) {
              for (const part of seriesData.parts) {
                const partDate = part.publishDate || story.publish_date;
                if (this.blogDAO.shouldShow(partDate, part.status || 'published', showFuture)) {
                  postsToProcess.push({
                    storyId: story.id,
                    partFolder: part.partFolder || part.folder, // Handle both field names
                    publishDate: partDate,
                    story,
                    seriesData,
                    partNumber: part.partNumber
                  });
                }
              }
            }
          }
        }
      }

      // Sort posts
      postsToProcess.sort((a, b) => {
        let comparison = 0;
        
        switch (sortBy) {
          case 'date':
            comparison = new Date(a.publishDate) - new Date(b.publishDate);
            break;
          case 'title':
            comparison = (a.story.title || '').localeCompare(b.story.title || '');
            break;
          case 'category':
            comparison = (a.story.category || '').localeCompare(b.story.category || '');
            break;
          default:
            comparison = new Date(a.publishDate) - new Date(b.publishDate);
        }
        
        return sortOrder === 'desc' ? -comparison : comparison;
      });

      // Apply pagination
      const paginatedPosts = limit || offset 
        ? postsToProcess.slice(offset, limit ? offset + limit : undefined)
        : postsToProcess;

      // Load and transform posts
      const posts = [];
      for (const postRef of paginatedPosts) {
        const storyData = await this.blogDAO.getStoryMetadata(
          postRef.storyId, 
          postRef.partFolder, 
          true // lightweight
        );
        
        if (storyData) {
          const routingEntry = {
            storyId: postRef.storyId,
            partFolder: postRef.partFolder || '',
            type: postRef.partFolder ? 'series' : 'standalone'
          };
          
          const post = await this.transformToPost(
            storyData, 
            postRef.story, 
            routingEntry, 
            postRef.seriesData,
            true // lightweight
          );
          
          // Find slug for this post
          post.slug = this.blogDAO.findRoutingKey(
            blogDb.routing, 
            postRef.storyId, 
            postRef.partFolder
          );
          post.url = `/blog/${post.slug}`;
          
          posts.push(post);
        }
      }

      return {
        posts,
        pagination: {
          total: postsToProcess.length,
          limit: limit || postsToProcess.length,
          offset: offset,
          hasMore: limit ? offset + limit < postsToProcess.length : false
        }
      };
    } catch (error) {
      console.error('Failed to get posts:', error);
      return { posts: [], pagination: { total: 0, limit: 0, offset: 0, hasMore: false } };
    }
  }

  // Get featured posts
  async getFeaturedPosts(limit = 6) {
    return this.getPosts({ featured: true, limit, sortBy: 'date', sortOrder: 'desc' });
  }

  // Get latest posts
  async getLatestPosts(limit = 10) {
    return this.getPosts({ limit, sortBy: 'date', sortOrder: 'desc' });
  }

  // Get posts by category
  async getPostsByCategory(category, options = {}) {
    return this.getPosts({ ...options, category });
  }

  // Get posts by tag
  async getPostsByTag(tag, options = {}) {
    return this.getPosts({ ...options, tag });
  }

  // Get posts by author
  async getPostsByAuthor(authorSlug, options = {}) {
    return this.getPosts({ ...options, author: authorSlug });
  }

  // RELATED POSTS AND RECOMMENDATIONS

  // Get related posts
  async getRelatedPosts(slug, limit = 5, currentPost = null) {
    try {
      // Only fetch if not provided
      if (!currentPost) {
        currentPost = await this.getPost(slug);
      }
      if (!currentPost) return [];


      // Get broader set of posts for better matching
      const allPostsResult = await this.getPosts({ 
        showFuture: false,
        limit: limit * 10 // Cast wider net for better matches
      });

      // Define low-value tags that don't help with differentiation
      const commonTags = ['AI', 'Series', 'Analysis']; // Tags that appear in >50% of posts
      
      // Score and filter related posts
      const scoredPosts = allPostsResult.posts
        .filter(post => {
          // Exclude current post
          if (post.slug === slug) return false;
          
          // Exclude same-series parts (redundant with series navigation)
          if (currentPost.series && post.series && 
              currentPost.series.name === post.series.name) {
            return false;
          }
          
          return true;
        })
        .map(post => {
          let score = 0;
          
          // Meaningful tag overlap scoring (exclude common tags)
          const currentMeaningfulTags = (currentPost.tags || [])
            .filter(tag => !commonTags.includes(tag));
          const postMeaningfulTags = (post.tags || [])
            .filter(tag => !commonTags.includes(tag));
          
          const meaningfulOverlap = this.tagDAO.calculateTagOverlap(
            currentMeaningfulTags, 
            postMeaningfulTags
          );
          score += meaningfulOverlap * 4; // Higher weight for meaningful tags
          
          // Category similarity (but lower weight than meaningful tags)
          if (currentPost.category === post.category) {
            score += 2;
          }
          
          // Cross-category relevance: different categories but related domains
          const crossCategoryBonus = this.calculateCrossCategoryRelevance(
            currentPost.category, 
            post.category
          );
          score += crossCategoryBonus;
          
          // Author relationship bonus
          if (currentPost.author && post.author && 
              currentPost.author.name === post.author.name) {
            score += 1;
          }
          
          // Reading time similarity
          const timeDiff = Math.abs((post.readTime || 0) - (currentPost.readTime || 0));
          if (timeDiff <= 3) score += 0.5;
          
          // Recency bonus (smaller impact)
          const daysDiff = (new Date() - new Date(post.publishDate)) / (1000 * 60 * 60 * 24);
          if (daysDiff <= 60) score += 0.5;

          return { ...post, relevanceScore: score };
        })
        .sort((a, b) => b.relevanceScore - a.relevanceScore)
        .slice(0, limit);

      return { 
        posts: scoredPosts,
        pagination: {
          total: scoredPosts.length,
          limit: limit,
          offset: 0,
          hasMore: false
        }
      };
    } catch (error) {
      console.error(`Failed to get related posts for ${slug}:`, error);
      return { posts: [], pagination: { total: 0, limit: limit, offset: 0, hasMore: false } };
    }
  }

  // Calculate cross-category relevance for related content
  calculateCrossCategoryRelevance(currentCategory, candidateCategory) {
    // Define related category mappings
    const categoryRelations = {
      'AI': ['Technology', 'Architecture', 'Sports Tech'],
      'Technology': ['AI', 'Architecture', 'Sports Tech'],
      'Architecture': ['AI', 'Technology'],
      'Sports Tech': ['AI', 'Technology', 'Healthcare'],
      'Healthcare': ['Sports Tech', 'AI'],
      'Cultural Analysis': ['Society', 'Politics & Economics'],
      'Society': ['Cultural Analysis', 'Politics & Economics'],
      'Politics & Economics': ['Society', 'Cultural Analysis']
    };

    const relatedCategories = categoryRelations[currentCategory] || [];
    return relatedCategories.includes(candidateCategory) ? 1 : 0;
  }

  // SERIES OPERATIONS

  // Get series navigation
  async getSeriesNavigation(slug) {
    try {
      const blogDb = await this.blogDAO.getBlogDatabase();
      const routingEntry = this.blogDAO.getRoutingEntry(blogDb.routing, slug);
      
      if (!routingEntry || routingEntry.type !== 'series') return null;

      const seriesData = await this.blogDAO.getSeriesMetadata(routingEntry.storyId);
      if (!seriesData) return null;

      const story = blogDb.stories.find(s => s.id === routingEntry.storyId);
      if (!story) return null;

      // Get all parts of the series
      const seriesRoutingEntries = this.blogDAO.getStoryRoutingEntries(
        blogDb.routing, 
        routingEntry.storyId
      );

      const parts = [];
      let currentIndex = -1;

      for (let i = 0; i < seriesData.parts.length; i++) {
        const part = seriesData.parts[i];
        const partFolder = part.partFolder || part.folder; // Handle both field names
        const partRouting = seriesRoutingEntries.find(r => r.partFolder === partFolder);
        
        if (partRouting) {
          const partData = await this.blogDAO.getStoryMetadata(
            routingEntry.storyId, 
            partFolder, 
            true // lightweight
          );

          const partInfo = {
            slug: partRouting.slug,
            title: partData?.title || `Part ${part.partNumber}`,
            partNumber: part.partNumber,
            readTime: partData?.readTime || 0,
            excerpt: partData?.excerpt || '',
            publishDate: part.publishDate || story.publish_date,
            isPublished: this.blogDAO.shouldShow(
              part.publishDate || story.publish_date, 
              part.status || 'published'
            ),
            isCurrent: partRouting.slug === slug
          };

          if (partInfo.isCurrent) {
            currentIndex = parts.length;
          }

          parts.push(partInfo);
        }
      }

      // Sort by part number
      parts.sort((a, b) => a.partNumber - b.partNumber);
      
      // Recalculate current index after sorting
      currentIndex = parts.findIndex(p => p.isCurrent);

      // Get previous/next published parts
      const publishedParts = parts.filter(p => p.isPublished);
      const currentPublishedIndex = publishedParts.findIndex(p => p.isCurrent);
      
      return {
        series: {
          name: seriesData.name,
          description: seriesData.description,
          currentPart: parts[currentIndex]?.partNumber,
          totalParts: seriesData.totalParts,
          estimatedReadTime: seriesData.estimatedReadTime
        },
        parts,
        previousPost: currentPublishedIndex > 0 ? publishedParts[currentPublishedIndex - 1] : null,
        nextPost: currentPublishedIndex < publishedParts.length - 1 ? publishedParts[currentPublishedIndex + 1] : null,
        currentIndex: currentPublishedIndex
      };
    } catch (error) {
      console.error(`Failed to get series navigation for ${slug}:`, error);
      return null;
    }
  }

  // SEARCH OPERATIONS

  // Search posts
  async searchPosts(query, options = {}) {
    if (!query || query.trim().length < 2) return { posts: [], query: query.trim() };

    const { limit = 20, includeContent = false } = options;
    const searchTerm = query.toLowerCase().trim();

    try {
      // Get all posts and search through them
      const allPosts = await this.getPosts({ showFuture: false });
      
      const searchResults = allPosts.posts
        .map(post => {
          let score = 0;
          
          // Title matching (highest priority)
          if (post.title.toLowerCase().includes(searchTerm)) {
            score += 10;
            if (post.title.toLowerCase().startsWith(searchTerm)) {
              score += 5; // Bonus for starts with
            }
          }
          
          // Excerpt matching
          if (post.excerpt && post.excerpt.toLowerCase().includes(searchTerm)) {
            score += 5;
          }
          
          // Tag matching
          if (post.tags && post.tags.some(tag => tag.toLowerCase().includes(searchTerm))) {
            score += 3;
          }
          
          // Category matching
          if (post.category && post.category.toLowerCase().includes(searchTerm)) {
            score += 2;
          }
          
          // Author name matching
          if (post.author && post.author.name && post.author.name.toLowerCase().includes(searchTerm)) {
            score += 1;
          }

          return score > 0 ? { ...post, searchScore: score } : null;
        })
        .filter(Boolean)
        .sort((a, b) => b.searchScore - a.searchScore)
        .slice(0, limit);

      return {
        posts: searchResults,
        query: searchTerm,
        total: searchResults.length
      };
    } catch (error) {
      console.error(`Failed to search posts for "${query}":`, error);
      return { posts: [], query: searchTerm, total: 0 };
    }
  }

  // UTILITY AND HELPER METHODS

  // Transform story data to post format
  async transformToPost(storyData, storyRef, routingEntry, seriesData = null, lightweight = false) {
    const cacheKey = `transform_${storyRef.id}_${routingEntry.partFolder || 'main'}_${lightweight}`;
    
    if (this.transformCache.has(cacheKey)) {
      return this.transformCache.get(cacheKey);
    }

    try {
      // Get author information
      const author = await this.authorDAO.getAuthor(storyRef.author_slug);
      
      // Get tag names
      const tagNames = await this.tagDAO.getTagNames(storyRef.tags || []);

      const post = {
        id: storyData.id || storyRef.id,
        title: storyData.title,
        excerpt: storyData.excerpt || '',
        author: author || { name: storyData.author || 'Unknown Author' },
        publishDate: storyData.publishDate || storyRef.publish_date,
        category: storyData.category || storyRef.category,
        tags: tagNames,
        featured: Boolean(storyRef.featured),
        readTime: storyData.readTime || 5,
        coverImage: this.resolveCoverImage(storyData, routingEntry.storyId, routingEntry.partFolder),
        storyId: routingEntry.storyId,
        partFolder: routingEntry.partFolder || null
      };

      // Add series information if applicable
      if (seriesData && routingEntry.type === 'series') {
        const currentPart = seriesData.parts?.find(p => 
          (p.partFolder || p.folder) === routingEntry.partFolder
        );
        if (currentPart) {
          post.series = {
            name: seriesData.name,
            description: seriesData.description,
            part: currentPart.partNumber,
            totalParts: seriesData.totalParts,
            estimatedReadTime: seriesData.estimatedReadTime
          };
        }
      }

      // Add SEO data if available
      if (storyData.seo) {
        post.seo = storyData.seo;
      }

      // Add audio version if available
      if (storyData.audioVersion) {
        post.audioVersion = {
          ...storyData.audioVersion,
          file: this.resolveAudioPath(storyData.audioVersion.file, routingEntry.storyId, routingEntry.partFolder)
        };
      }

      // Add formatted date for UI components
      post.dateFormatted = this.formatDate(post.publishDate);
      post.date = post.publishDate; // Keep original date for compatibility

      // Cache the transformation
      this.transformCache.set(cacheKey, post);
      
      return post;
    } catch (error) {
      console.error('Failed to transform post:', error);
      throw error;
    }
  }

  // Resolve cover image path
  resolveCoverImage(storyData, storyId, partFolder = null) {
    // Check for explicit cover image in story data
    if (storyData.coverImage) {
      if (storyData.coverImage.startsWith('/')) {
        return storyData.coverImage; // Absolute path
      } else {
        // Relative path - construct full path
        const basePath = partFolder 
          ? `/blog/stories/${storyId}/${partFolder}`
          : `/blog/stories/${storyId}`;
        return `${basePath}/${storyData.coverImage}`;
      }
    }

    // Default cover image path
    const basePath = partFolder 
      ? `/blog/stories/${storyId}/${partFolder}`
      : `/blog/stories/${storyId}`;
    return `${basePath}/cover.webp`;
  }

  // Resolve audio file path
  resolveAudioPath(audioFile, storyId, partFolder = null) {
    if (!audioFile) return null;
    
    if (audioFile.startsWith('/')) {
      return audioFile; // Absolute path
    } else {
      // Relative path - construct full path
      const basePath = partFolder 
        ? `/blog/stories/${storyId}/${partFolder}`
        : `/blog/stories/${storyId}`;
      return `${basePath}/${audioFile}`;
    }
  }

  // Process markdown to HTML
  processMarkdownToHtml(markdown) {
    if (!markdown) return '';
    
    // Configure marked options similar to blogEngine
    marked.setOptions({
      breaks: true,
      gfm: true,
      tables: true,
      pedantic: false,
      sanitize: false,
      smartLists: true,
      smartypants: true
    });
    
    let html = marked(markdown);
    
    // Remove the first H1 tag from the content since we show the title in the header
    html = html.replace(/^<h1[^>]*>.*?<\/h1>\s*/i, '');
    
    return html;
  }

  // Calculate reading time from markdown content
  calculateReadTime(markdown) {
    if (!markdown) return 0;
    
    const wordsPerMinute = 200;
    const wordCount = this.getWordCount(markdown);
    return Math.max(1, Math.ceil(wordCount / wordsPerMinute));
  }

  // Get word count from markdown
  getWordCount(markdown) {
    if (!markdown) return 0;
    
    // Remove markdown syntax and count words
    const text = markdown
      .replace(/[#*`_~\[\]()]/g, '') // Remove markdown chars
      .replace(/!\[.*?\]\(.*?\)/g, '') // Remove images
      .replace(/\[.*?\]\(.*?\)/g, '') // Remove links
      .replace(/```[\s\S]*?```/g, '') // Remove code blocks
      .replace(/`[^`]*`/g, '') // Remove inline code
      .trim();
    
    return text ? text.split(/\s+/).length : 0;
  }

  // Get blog statistics
  async getStats() {
    try {
      const blogDb = await this.blogDAO.getBlogDatabase();
      const currentDate = new Date();
      
      const publishedStories = blogDb.stories.filter(story => 
        this.blogDAO.shouldShow(story.publish_date, story.publishStatus)
      );

      const allAuthors = await this.authorDAO.getAllAuthors();
      const tagStats = await this.tagDAO.getTagStats();
      const usedTags = tagStats.filter(tag => tag.count > 0);

      return {
        totalPosts: publishedStories.length,
        totalAuthors: allAuthors.length,
        totalTags: usedTags.length,
        featuredPosts: publishedStories.filter(s => s.featured).length,
        categories: [...new Set(publishedStories.map(s => s.category))].length,
        lastUpdated: blogDb.settings?.lastUpdated || new Date().toISOString()
      };
    } catch (error) {
      console.error('Failed to get blog stats:', error);
      return {
        totalPosts: 0,
        totalAuthors: 0,
        totalTags: 0,
        featuredPosts: 0,
        categories: 0,
        lastUpdated: new Date().toISOString()
      };
    }
  }

  // CATEGORY AND SERIES OPERATIONS

  // Get all categories with post counts
  async getCategories() {
    try {
      const blogDb = await this.blogDAO.getBlogDatabase();
      const categoryMap = new Map();
      
      // Single pass through stories in memory
      for (const story of blogDb.stories) {
        if (story.category && this.blogDAO.shouldShow(story.publish_date, story.publishStatus, false)) {
          if (!categoryMap.has(story.category)) {
            categoryMap.set(story.category, { 
              name: story.category, 
              count: 0,
              slug: story.category.toLowerCase().replace(/[^a-z0-9]+/g, '-')
            });
          }
          categoryMap.get(story.category).count++;
        }
      }
      
      return Array.from(categoryMap.values()).sort((a, b) => b.count - a.count);
    } catch (error) {
      console.error('Failed to get categories:', error);
      return [];
    }
  }

  // Get all series with metadata
  async getSeries() {
    try {
      const blogDb = await this.blogDAO.getBlogDatabase();
      const seriesList = [];
      
      for (const story of blogDb.stories) {
        if (story.type === 'series' && this.blogDAO.shouldShow(story.publish_date, story.publishStatus, false)) {
          const seriesData = await this.blogDAO.getSeriesMetadata(story.id);
          if (seriesData) {
            // Count published parts
            const publishedParts = seriesData.parts.filter(part => {
              const partDate = new Date(part.publishDate || story.publish_date);
              return partDate <= new Date();
            }).length;
            
            seriesList.push({
              id: story.id,
              name: seriesData.name,
              description: seriesData.description,
              totalParts: seriesData.totalParts,
              publishedParts: publishedParts,
              category: story.category,
              author: story.author_slug,
              startDate: story.publish_date,
              featured: story.featured || false,
              estimatedReadTime: seriesData.estimatedReadTime || null
            });
          }
        }
      }
      
      return seriesList.sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
    } catch (error) {
      console.error('Failed to get series:', error);
      return [];
    }
  }

  // Get series by name
  async getSeriesByName(seriesName) {
    try {
      const allSeries = await this.getSeries();
      return allSeries.find(s => s.name === seriesName) || null;
    } catch (error) {
      console.error(`Failed to get series by name ${seriesName}:`, error);
      return null;
    }
  }

  // Clear all caches
  clearCache() {
    this.blogDAO.clearCache();
    this.authorDAO.clearCache();
    this.tagDAO.clearCache();
    this.transformCache.clear();
  }

  // Format date utility
  formatDate(dateString, format = 'long') {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';

    const options = {
      long: { year: 'numeric', month: 'long', day: 'numeric' },
      short: { year: 'numeric', month: 'short', day: 'numeric' },
      compact: { month: 'short', day: 'numeric' }
    };

    return date.toLocaleDateString('en-US', options[format] || options.long);
  }
}